import { useCallback, useEffect, useRef, useState } from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { GenericExternalLink } from "@components/generic-external-link";
import { useUser } from "@components/user-context";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type FetchSpreadsheets } from "@north-beam/nb-client-side";
import { FETCH_SPREADSHEETS } from "@pages/data-spreadsheets";
import { useApiKeys } from "@pages/settings/api-keys";
import { ServerDiagnosticsDisplay } from "@pages/settings/pixel-diagnostics";
import { PrimaryButton } from "@shared/buttons";
import { Button } from "@shared/buttons/button";
import RadioGroup from "@shared/radio-group";

import { scrollToSubStepElement } from "../onboarding-nav/scroll-utils";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { LinkClipboardButton } from "./components/link-clipboard-button";
import { WarningPanel } from "./components/warning-panel";
import { Platform } from "./configure-platforms/platforms/constants";
import { usePlatformsMetadata } from "./configure-platforms/platforms/metadata";
import { DocumentationInfo } from "./orders-api/order-api-tags";
import { SpendSheet } from "./spend-api/spend-sheet";
import {
  useSpendSheetConfirmation,
  type TrackSpendFrequency,
  type TrackSpendGranularity,
} from "./spend-api/spend-sheet-confirmation";
import { SpendSheetPlaceholder } from "./spend-api/spend-sheet-placeholder";
import { Code, Table } from "./spend-api/styled";
import { Step, type StepChildProps } from "./step";
import { StepCompletionConfirmation } from "./step-completion-confirmation";
import { StepContainer } from "./step-container";

const SPEND_PLATFORMS = [
  Platform.ATTENTIVE,
  Platform.AD_ROLL,
  Platform.AVANT_LINK,
  Platform.BLISS_POINT_MEDIA,
  Platform.CNN5_THINGS,
  Platform.CRITEO,
  "Geist",
  Platform.GRIN,
  Platform.IMPACT_RADIUS,
  Platform.KLAVIYO,
  Platform.LIVE_INTENT,
  Platform.OUTBRAIN,
  Platform.PARTNERIZE,
  Platform.POSTSCRIPT,
  Platform.RAKUTEN,
  Platform.RETENTION_SCIENCE,
  Platform.REVCONTENT,
  Platform.SHARE_A_SALE,
  Platform.STACK_ADAPT,
  Platform.TABOOLA,
  Platform.TRADE_DESK,
  Platform.YAHOO_DSP,
];

const NEXT_STEP_TOOLTIP =
  "All steps must be completed to advance to Verify and Finish";

interface StepMetadata {
  useSpendApi?: boolean;
  googleSheetConfig?: {
    frequency?: TrackSpendFrequency;
    granularity?: TrackSpendGranularity;
    confirmed?: boolean;
  };
}

export const SpendApiStep: React.FC<StepChildProps> = ({
  nextStep,
  updateStepMetadata: _updateStepMetadata,
  stepMetadata: _stepMetadata,
  getStepMetadata,
  isMarkedComplete,
  toggleStepComplete,
  canVerifyAndFinish,
  ...props
}) => {
  const { designElementManager } = useDesignElementManager();

  const { getTitleForPlatform } = usePlatformsMetadata();

  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
    step: withDefault(StringParam, ""),
  });

  const aScrollRef = useRef<(() => void) | null>(null);
  const bScrollRef = useRef<(() => void) | null>(null);
  const cScrollRef = useRef<(() => void) | null>(null);

  const aTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        aScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "a");
      }
    },
    [searchParams],
  );
  const bTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        bScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "b");
      }
    },
    [searchParams],
  );
  const cTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        cScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "c");
      }
    },
    [searchParams],
  );

  const [stepMetadata, setStepMetadata] = useState<StepMetadata>(_stepMetadata);

  const updateStepMetadata = useCallback(
    async (update: StepMetadata) => {
      setStepMetadata(update);
      await _updateStepMetadata(update);
    },
    [_updateStepMetadata],
  );

  const { user } = useUser();
  const {
    apiKeys,
    loading: apiKeysLoading,
    createNewApiKey,
    createNewApiKeyLoading,
  } = useApiKeys();

  const { open: openSpendSheetModal, Modal: SpendSheetModal } =
    useSpendSheetConfirmation({
      onConfirm: (frequency, granularity) => {
        updateStepMetadata({
          googleSheetConfig: {
            confirmed: true,
            frequency: frequency,
            granularity: granularity,
          },
        });
      },
    });

  const [hasSpendSheet, setHasSpendSheet] = useState(false);
  const spendSheetQueryResult = useNorthbeamQuery<FetchSpreadsheets>(
    FETCH_SPREADSHEETS,
    {},
  );
  useEffect(() => {
    if (spendSheetQueryResult.loading) {
      return;
    }
    setHasSpendSheet(
      (spendSheetQueryResult.data?.me.customSpendSheets || []).length > 0,
    );
  }, [spendSheetQueryResult]);
  const [expandedHelp, setExpandedHelp] = useState(false);
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["spend-api"].index;

  const selectedPlatforms = SPEND_PLATFORMS.filter((platform) =>
    Object.keys(
      getStepMetadata(Steps.CONFIGURE_PLATFORMS)?.platforms || {},
    ).includes(platform),
  );

  const hasRequiredSpendPlatforms = selectedPlatforms.length;
  const isNextStepDisabled =
    !canVerifyAndFinish && (!stepMetadata?.googleSheetConfig || hasSpendSheet);

  const nextStepValue = Steps.VERIFY;

  return (
    <StepContainer
      title={`${stepIndex}. Spend API`}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.CONFIGURE_PLATFORMS,
        });
        props.previousStep?.();
      }}
      onMoveForwardButtonClicked={() => {
        setSearchParams({
          step: nextStepValue,
        });
        nextStep?.();
      }}
      moveForwardButtonDisabled={isNextStepDisabled}
      moveForwardButtonTooltip={isNextStepDisabled && NEXT_STEP_TOOLTIP}
      knowledgeBaseLink="https://docs.northbeam.io/docs/spend-api"
      knowledgeBaseText="Spend API"
      stepCompletionConfirmation={
        (!stepMetadata?.googleSheetConfig || hasSpendSheet) && (
          <StepCompletionConfirmation
            labelSuffix="configuring the ‘Spend API’"
            step={Steps.SPEND_API}
            nextStep={() => {
              if (!isMarkedComplete) {
                toggleStepComplete();
              }
              setSearchParams({
                step: nextStepValue,
              });
              nextStep && nextStep();
            }}
            nextStepDisabled={isNextStepDisabled}
            nextStepLabel={Steps.getAttribute(nextStepValue, "navConfig").label}
            nextStepTooltip={isNextStepDisabled && NEXT_STEP_TOOLTIP}
            toggleStepComplete={toggleStepComplete}
          />
        )
      }
    >
      <Step
        className="[&_h1]:text-2xl [&_h1]:font-semibold
          [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:p-0 [&_h2]:m-0"
        nextDisabled={isNextStepDisabled}
        nextStepButtonText={`${stepDictionary["verify"].index}. Verify Your Setup`}
        nextStepButtonTooltip={isNextStepDisabled && NEXT_STEP_TOOLTIP}
        nextStep={() => {
          if (!isMarkedComplete) {
            toggleStepComplete();
          }
          setSearchParams({
            step: nextStepValue,
          });
          nextStep?.();
        }}
      >
        {SpendSheetModal}
        <div
          ref={aTitleRef}
          className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-10 inline-flex border-b-gray-1 border-b"
        >
          {hasRequiredSpendPlatforms ? (
            <>
              <h1>
                Some of your marketing platforms require additional Spend data.
              </h1>
              <h2>
                These are the platforms you’ll need to add Spend data for:
              </h2>
              <div className="w-full p-6 border rounded-lg inline-flex flex-col gap-10 bg-cosmic-5">
                <div className="inline-flex flex-col gap-4">
                  <div
                    className={`grid grid-flow-col grid-rows-${
                      // ideally, would have Math.trunc(selectedPlatforms.length / 5) + 1,
                      // but for some reason grid-rows is only working with 1, 2, and 5...
                      selectedPlatforms.length < 5
                        ? 1
                        : selectedPlatforms.length <= 10
                        ? 2
                        : 5
                    } gap-6 justify-evenly items-center overflow-hidden`}
                  >
                    {selectedPlatforms.map((platform) => (
                      <div
                        key={platform}
                        className="w-[140px] h-6 inline-flex gap-2 text-sm"
                      >
                        {designElementManager
                          .getDesignElement(platform)
                          .getComponent()}
                        <span>{getTitleForPlatform(platform as Platform)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="w-full inline-flex flex-col">
                <h2>
                  You’ll also need to add spend data for any platform tracked
                  using Generic UTM guidelines{" "}
                </h2>
                <p>
                  This will include any platform you added using the “
                  <a>Any Other Platform</a>” option in the previous step
                </p>
                <div className="px-10 py-6 border rounded-lg bg-cosmic-5 flex flex-col gap-4">
                  <h3 className="m-0 p-0 text-sm font-semibold">
                    A common example is your email marketing platform:
                  </h3>
                  <div className="flex gap-6 items-center">
                    <div className="flex gap-2">
                      <div className="h-6 w-6 rounded-full border border-gray-1 flex justify-center items-center bg-sun-2">
                        <MailchimpIcon />
                      </div>
                      <span>Mailchimp</span>
                    </div>
                    <span className="uppercase text-cosmic font-semibold text-sm">
                      or
                    </span>
                    <div className="flex gap-2">
                      <div className="h-6 w-6 rounded-full border border-gray-1 flex justify-center items-center bg-pluto-2">
                        <SendGridIcon />
                      </div>
                      <span>SendGrid</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full inline-flex flex-col">
              <h1>
                Do you have any other additional Spend? (If not, tick the
                'Complete' checkbox at the bottom of the screen)
              </h1>
              <p>
                The Spend API / Spend Sheet allows you to add platforms you
                added using the “<a>Other Platform</a>” option in the previous
                step, or any other platforms that you care about tracking.
              </p>
              <div className="px-10 py-6 border rounded-lg bg-cosmic-5 flex flex-col gap-4">
                <h3 className="m-0 p-0 text-sm font-semibold">
                  A common example is your email marketing platform:
                </h3>
                <div className="flex gap-6 items-center">
                  <div className="flex gap-2">
                    <div className="h-6 w-6 rounded-full border border-gray-1 flex justify-center items-center bg-sun-2">
                      <MailchimpIcon />
                    </div>
                    <span>Mailchimp</span>
                  </div>
                  <span className="uppercase text-cosmic font-semibold text-sm">
                    or
                  </span>
                  <div className="flex gap-2">
                    <div className="h-6 w-6 rounded-full border border-gray-1 flex justify-center items-center bg-pluto-2">
                      <SendGridIcon />
                    </div>
                    <span>SendGrid</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <hr className="w-full bg-gray" />
          <div className="w-full flex flex-col gap-10">
            <h1 className="m-0 p-0">Tell us how you’d like to track Spend</h1>
            <RadioGroup
              className="text-lg space-y-6"
              options={[
                {
                  label: (
                    <span className="text-lg leading-normal">
                      Northbeam Spend API
                      <span className="ml-2 py-1 pl-1 pr-[12px] rounded-lg bg-alien-2 text-alien text-xs text-center font-semibold">
                        <i className="px-1 fa-sharp fa-regular fa-check font-black" />
                        Recommended
                      </span>
                    </span>
                  ),
                  value: "api",
                  disabledTooltip:
                    "Uncheck the 'Complete' checkbox at the bottom of the screen to update",
                  disabled:
                    isMarkedComplete ||
                    (!!stepMetadata?.googleSheetConfig && hasSpendSheet),
                },
                {
                  label: (
                    <span className="text-lg leading-normal">
                      {stepMetadata?.googleSheetConfig && hasSpendSheet && (
                        <i className="mr-2 fa-regular fa-lock font-black text-gray text-sm" />
                      )}
                      Google Sheet
                      <span className="ml-2 py-1 pl-1 pr-[12px] rounded-lg bg-sun-2 text-sun+2 text-xs text-center font-semibold">
                        <i className="px-1 fa-sharp fa-regular fa-warning font-black" />
                        Not Recommended
                      </span>
                    </span>
                  ),
                  disabledTooltip:
                    "Uncheck the 'Complete' checkbox at the bottom of the screen to update",
                  value: "sheet",
                  disabled:
                    isMarkedComplete ||
                    (!!stepMetadata?.googleSheetConfig && hasSpendSheet),
                },
              ]}
              value={
                stepMetadata?.useSpendApi
                  ? "api"
                  : stepMetadata?.googleSheetConfig
                  ? "sheet"
                  : ""
              }
              onChange={(value) => {
                if (value === "api") {
                  updateStepMetadata({
                    useSpendApi: true,
                  });
                } else {
                  openSpendSheetModal();
                }
              }}
            />
            {stepMetadata?.googleSheetConfig && !hasSpendSheet && (
              <div className="py-6 px-4 border rounded bg-alien-2 text-lg leading-normal">
                <i className="fa-regular fa-envelope mr-2 text-alien"></i>
                <span className="font-semibold">
                  Contact your Onboarding Manager to get this process started!
                </span>
              </div>
            )}
            {stepMetadata?.googleSheetConfig && hasSpendSheet && (
              <WarningPanel>
                <span className="font-semibold">
                  <b>
                    Your Google Sheet has been created, if you would like to
                    switch to the Spend API, please{" "}
                  </b>
                  <GenericExternalLink href="https://www.northbeam.io/submit-a-support-ticket">
                    contact us
                  </GenericExternalLink>{" "}
                  to help you get that sorted.
                </span>
              </WarningPanel>
            )}
            <div className="flex flex-col gap-4">
              <h2>Benefits of each option</h2>
              <Table className="bg-white">
                <thead>
                  <tr>
                    <th className="w-1/2 border-r border-gray-2">
                      Northbeam Spend API
                    </th>
                    <th>Google Sheet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-3 odd:bg-cosmic-5 even:bg-white">
                    <td className="border-r border-gray-2 text-sm leading-tight">
                      <i className="mr-2 fa-sharp fa-regular fa-check text-alien font-black" />
                      Enables faster and less error prone data uploads
                    </td>
                    <td rowSpan={5}>
                      <div className="flex flex-col gap-2 items-center justify-center">
                        <i className="fa-sharp fa-regular fa-warning font-black text-lg text-sun+2" />
                        <p className="w-[320px] text-center">
                          Only use a Google Sheet if your brand does not have an
                          engineering team to configure the Spend API
                        </p>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-t border-gray-2 odd:bg-cosmic-5 even:bg-white">
                    <td className="border-r border-gray-2 text-sm leading-tight">
                      <i className="mr-2 fa-sharp fa-regular fa-check text-alien font-black" />
                      Makes it easy to track multiple channels simultaneously{" "}
                    </td>
                  </tr>
                  <tr className="border-t border-gray-2 odd:bg-cosmic-5 even:bg-white">
                    <td className="border-r border-gray-2 text-sm leading-tight">
                      <i className="mr-2 fa-sharp fa-regular fa-check text-alien font-black" />
                      More robust solution
                    </td>
                  </tr>
                  <tr className="border-t border-gray-2 odd:bg-cosmic-5 even:bg-white">
                    <td className="border-r border-gray-2 text-sm leading-tight">
                      <i className="mr-2 fa-sharp fa-regular fa-check text-alien font-black" />
                      Easier troubleshooting and better support from our team
                    </td>
                  </tr>
                  <tr className="border-t border-gray-2 odd:bg-cosmic-5 even:bg-white">
                    <td className="border-r border-gray-2 text-sm leading-tight">
                      <i className="mr-2 fa-sharp fa-regular fa-check text-alien font-black" />
                      Ability to track metrics at the Adset and Ad level (in
                      addition to Campaign level)
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
        {stepMetadata?.useSpendApi && (
          <>
            <div
              ref={bTitleRef}
              className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-10 inline-flex "
            >
              <div className="flex flex-col gap-10">
                <h1>Add spend tracking to Northbeam using the ‘Spend API’</h1>
                <DocumentationInfo
                  docTitle="Learn more about the Spend API"
                  link="https://docs.northbeam.io/reference/get_spend"
                  iconClassName="fa-sharp fa-solid fa-book"
                />
                <div className="inline-flex flex-col">
                  <h2>
                    1. Use your ‘Data-client-ID’ and ‘API Key’ to set up the
                    ‘Spend API’.
                  </h2>
                  <p className="mt-2 mb-4">
                    No need to be a full blown developer, just insert your API
                    keys and populate an example payload with some sample data
                    (use our example values if unsure) to make an API call to
                    our development or production endpoints. The Reference page
                    also features example code for Shell, Ruby, PHP, Python and
                    my other languages.
                  </p>
                  <PrimaryButton
                    className="w-fit py-1.5 px-4 text-xs uppercase"
                    onClick={() => {
                      window.open(
                        "https://docs.northbeam.io/reference/get_spend",
                        "_blank",
                      );
                    }}
                  >
                    <i className="fa-sharp fa-regular fa-share mr-2" />
                    Add keys in the Spend API reference
                  </PrimaryButton>
                </div>
                <div className="inline-flex flex-col gap-4">
                  <b>Your ‘Data-client-ID’ and ‘API Key’</b>
                  <span>
                    You can use the same ones from the the ‘Orders API’ step
                  </span>
                  {!apiKeysLoading && (
                    <div className="flex flex-col gap-4">
                      <Table>
                        <thead>
                          <tr>
                            <th className="border-r">Data-client-id</th>
                            <th>API Key</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apiKeys.map((key) => (
                            <tr
                              key={key}
                              className="odd:bg-cosmic-5 even:bg-white border-t"
                            >
                              <td className="border-r">
                                <div className="w-full inline-flex justify-between items-center">
                                  <code className="text-interstellar">
                                    {user.displayUserId}
                                  </code>
                                  <LinkClipboardButton
                                    value={user.displayUserId}
                                    name="Data-client-id"
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="w-full inline-flex justify-between items-center">
                                  <code className="text-interstellar">
                                    {key}
                                  </code>
                                  <LinkClipboardButton
                                    value={key}
                                    name="API Key"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="inline-flex gap-4 items-center">
                        <Button
                          className="w-[200px] px-4 py-1.5 space-x-2 uppercase"
                          variant="secondary"
                          onClick={() => {
                            createNewApiKey();
                          }}
                          loading={createNewApiKeyLoading}
                          disabled={createNewApiKeyLoading || isMarkedComplete}
                        >
                          <i className="fa-sharp fa-regular fa-refresh" />
                          <span className="font-semibold">
                            Generate a new API Key
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="inline-flex flex-col gap-2">
                  <b>Generating an API Key will yield:</b>
                  <ul className="list-disc mb-1 ml-4">
                    <li>
                      <span>
                        <Code>Data-client-ID</Code> a UUID used to identify you
                        as the customer
                      </span>
                    </li>
                    <li>
                      <span>
                        <Code>API Key</Code> used to Authenticate the API call
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="w-full h-px bg-gray" />
              <div className="inline-flex flex-col gap-10">
                <h2>2. Add Spend data using the Spend API.</h2>
                <DocumentationInfo
                  docTitle="Spend API Reference"
                  link="https://docs.northbeam.io/reference/post_spend"
                  iconClassName="fa-sharp fa-solid fa-book"
                />
                <div>
                  <b>Required attributes</b>
                  <p>
                    Certain metadata is required for a spend entry to be
                    submitted. Without these fields, an API call will return an
                    error as part of our validation check to make sure the spend
                    entries contained in an API call pass muster.{" "}
                  </p>
                  <Table className="my-4">
                    <thead>
                      <tr>
                        <th className="w-min-[178px] border-r">Attribute</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          attr: "date",
                          description:
                            "The date the spend was committed, in a YYYY-MM-DD fashion",
                        },
                        {
                          attr: "platform_name",
                          description:
                            // eslint-disable-next-line max-len
                            "This field denotes the platform name the spend object will appear under in the dashboard. This must be supplied as a non-empty string.",
                        },
                        {
                          attr: "campaign_id",

                          description:
                            // eslint-disable-next-line max-len
                            "While the ad hierarchies of each ad platform are different, each platform has a least one level of ads to enumerate over. Within northbeam, we consider campaigns to be the highest level ad object. Use this field to capture the ID of the ad object you are spending on",
                        },
                        {
                          attr: "campaign_name",
                          description:
                            "This field denotes the campaign name the spend object will appear under in the dashboard.",
                        },
                        {
                          attr: "spend",
                          description:
                            // eslint-disable-next-line max-len
                            "This captures how much was spent on an ad object on a given day. If no spend was made on an ab object, consider not sending an entry for that ad object.",
                        },
                        {
                          attr: "spend_currency",
                          description:
                            "This field captures the currency that was used for the spend, per ISO-4217 standards (a 3 letter denomination)",
                        },
                      ].map(({ attr, description }) => (
                        <tr
                          key={attr}
                          className="odd:bg-cosmic-5 even:bg-white border-t"
                        >
                          <td className="border-r text-xs">
                            <code>{attr}</code>
                          </td>
                          <td className="text-sm">{description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <b>Optional attributes</b>
                  <p>
                    See the full list of attributes in the{" "}
                    <a
                      href="https://docs.northbeam.io/reference/post_spend"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Spend API Reference
                    </a>
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <b>Endpoint URLs:</b>
                  <Table>
                    <thead>
                      <tr>
                        <th className="min-w-[324px] border-r">URL</th>
                        <th className="min-w-[98px] border-r">Purpose</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="odd:bg-cosmic-5 even:bg-white border-t">
                        <td className="border-r">
                          <div className="flex justify-between items-center text-xs">
                            <code className="text-interstellar">
                              https://api-uat.northbeam.io/v1/spend
                            </code>
                            <LinkClipboardButton
                              value="https://api-uat.northbeam.io/v1/spend"
                              name="Testing URL"
                            />
                          </div>
                        </td>
                        <td className="border-r">Testing</td>
                        <td>For testing the API endpoint</td>
                      </tr>
                      <tr className="odd:bg-cosmic-5 even:bg-white border-t">
                        <td className="border-r text-xs">
                          <div className="flex justify-between items-center">
                            <code className="text-interstellar">
                              https://api.northbeam.io/v1/spend
                            </code>
                            <LinkClipboardButton
                              value="https://api.northbeam.io/v1/spend"
                              name="Production URL"
                            />
                          </div>
                        </td>
                        <td className="border-r">Production</td>
                        <td>
                          Spend data sent via this base URL will be used for ad
                          attribution (MTA) and predictive analytics (MMM)
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
            <hr className="w-full bg-gray" />
            <div className="mx-10 my-8 ">
              <div ref={cTitleRef} className="w-full flex flex-col gap-5">
                <h1>Verify there are no errors</h1>
                <p className="mb-2">
                  If you do encounter errors, please address them using the
                  Spend API Error table and graph below.
                </p>
                <ServerDiagnosticsDisplay spendApi={true} ordersApi={false} />
              </div>
            </div>
          </>
        )}
        {stepMetadata?.googleSheetConfig && (
          <div className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-10 inline-flex border-b-gray-1 border-b">
            <h1>Start adding spend data in your Google Sheet</h1>
            <SpendSheet
              queryResult={spendSheetQueryResult}
              placeholder={
                <SpendSheetPlaceholder>
                  <span className="font-semibold text-gray+1 text-lg leading-normal">
                    We’ll prepare your G-Sheet
                    <br /> and drop it here once it’s ready!
                  </span>
                </SpendSheetPlaceholder>
              }
            />
          </div>
        )}
      </Step>
    </StepContainer>
  );
};

const MailchimpIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="14" height="14" fill="url(#pattern0_3076_141977)" />
    <defs>
      <pattern
        id="pattern0_3076_141977"
        patternContentUnits="objectBoundingBox"
        width="1"
        height="1"
      >
        <use xlinkHref="#image0_3076_141977" transform="scale(0.00195312)" />
      </pattern>
      <image
        id="image0_3076_141977"
        width="512"
        height="512"
        // eslint-disable-next-line max-len
        xlinkHref="data:image/png;base64,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"
      />
    </defs>
  </svg>
);

const SendGridIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_3076_141983)">
      <path d="M4.00003 4H0V12H8.00002V4H4.00003Z" fill="#9DE1F3" />
      <path d="M4 0V7.99997H12V0H4Z" fill="#27B4E1" />
      <path
        d="M0 12H3.99998V8.00002H0V12ZM8.00002 3.99998H12V0H8.00002V3.99998Z"
        fill="#1A82E2"
      />
      <path d="M4 7.99998H7.99998V4H4V7.99998Z" fill="#239FD7" />
    </g>
    <defs>
      <clipPath id="clip0_3076_141983">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
